import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLine, scrollToId } from "./anim";

const HERO_IMG =
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2400&q=80";

const LINES = [
  { text: "Build wealth with" },
  { text: "greater confidence," },
  { text: "clarity, and a" },
  { text: "strategic plan.", gold: true },
];

export default function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 40, damping: 18 });
  const smy = useSpring(my, { stiffness: 40, damping: 18 });
  const parX = useTransform(smx, [-1, 1], [-12, 12]);
  const parY = useTransform(smy, [-1, 1], [-8, 8]);
  const glowX = useTransform(smx, [-1, 1], ["25%", "75%"]);
  const glowY = useTransform(smy, [-1, 1], ["30%", "70%"]);

  const onMouseMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 2);
    my.set((e.clientY / window.innerHeight - 0.5) * 2);
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div style={{ y: imgY, x: parX }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Modern architectural facade rising into darkness, a study in structure and ambition"
          className="h-[120%] w-full scale-105 object-cover brightness-[0.62]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-[#08080A]/50 to-[#08080A]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08080A]/70 via-[#08080A]/20 to-transparent" />
      </motion.div>

      <motion.div
        aria-hidden="true"
        style={{ left: glowX, top: glowY }}
        className="pointer-events-none absolute h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(197,160,89,0.14),transparent_65%)] blur-2xl"
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 sm:px-8">
        <MaskedLine delay={0.15}>
          <span
            data-testid="hero-badge"
            className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#F9F8F6]/90 backdrop-blur-md"
          >
            <img
              src="/assets/logo.jpeg"
              alt=""
              aria-hidden="true"
              className="h-5 w-5 rounded-full border border-[#C5A059]/40 object-cover object-top"
            />
            Kingdom Wealth Enterprises, LLC
          </span>
        </MaskedLine>

        <h1
          data-testid="hero-headline"
          className="mt-8 max-w-4xl text-4xl font-light uppercase leading-[1.06] tracking-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl"
        >
          {LINES.map((line, i) => (
            <MaskedLine key={line.text} delay={0.3 + i * 0.12}>
              <span className={line.gold ? "font-normal text-[#C5A059]" : ""}>{line.text}</span>
            </MaskedLine>
          ))}
        </h1>

        <MaskedLine delay={0.85}>
          <p data-testid="hero-subheadline" className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#C9C9CF] sm:text-lg">
            For entrepreneurs, business owners, and investors facing a major decision, we provide the
            experienced, objective consulting that turns uncertainty into informed action. You bring the
            ambition. We bring the strategy.
          </p>
        </MaskedLine>

        <MaskedLine delay={1.0}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-schedule-cta" onClick={onBook} className="btn-gold">
              Schedule a Consultation
            </button>
            <button data-testid="hero-explore-cta" onClick={() => scrollToId("approach")} className="btn-ghost">
              Explore Our Approach
            </button>
          </div>
        </MaskedLine>

        <MaskedLine delay={1.15}>
          <div className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-6 text-[10px] font-medium uppercase tracking-[0.28em] text-[#71717A]">
            <span>Business Strategy</span>
            <span className="hidden h-px w-8 bg-[#C5A059]/50 sm:block" aria-hidden="true" />
            <span>Opportunity Analysis</span>
            <span className="hidden h-px w-8 bg-[#C5A059]/50 sm:block" aria-hidden="true" />
            <span>Wealth-Building</span>
            <span className="ml-auto hidden items-center gap-2 text-[#A1A1AA] md:flex">
              Scroll
              <motion.span
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowDown size={13} />
              </motion.span>
            </span>
          </div>
        </MaskedLine>
      </motion.div>
    </section>
  );
}
